import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
// import AdminMessageMonitoring from '../components/AdminMessageMonitoring'

interface LoginAttempt {
  username: string
  password: string
  timestamp: string
  success: boolean
  ip?: string
}

export default function AdminDashboard() {
  const { user } = useAuth()
  const [loginAttempts, setLoginAttempts] = useState<LoginAttempt[]>([])
  const [activeTab, setActiveTab] = useState<'overview' | 'login-logs' | 'messaging'>('overview')

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      return
    }

    const attempts = JSON.parse(localStorage.getItem('loginAttempts') || '[]')
    setLoginAttempts(attempts.reverse()) // Show newest first
  }, [user])

  const clearLogs = () => {
    localStorage.setItem('loginAttempts', '[]')
    setLoginAttempts([])
  }

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString()
  }

  const getStats = () => {
    const total = loginAttempts.length
    const successful = loginAttempts.filter(a => a.success).length
    const failed = total - successful
    const uniqueUsers = new Set(loginAttempts.filter(a => a.success).map(a => a.username)).size

    return { total, successful, failed, uniqueUsers }
  }

  const stats = getStats()

  if (!user || user.role !== 'admin') {
    return (
      <div className="admin-access-denied">
        <h2>Access Denied</h2>
        <p>This page is only accessible to administrators.</p>
      </div>
    )
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <p>Logged in as: <strong>{user.username}</strong> ({user.role})</p>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button 
            className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📊 Overview
          </button>
          <button 
            className={`tab-btn ${activeTab === 'messaging' ? 'active' : ''}`}
            onClick={() => setActiveTab('messaging')}
          >
            💬 Message Monitoring
          </button>
          <button 
            className={`tab-btn ${activeTab === 'login-logs' ? 'active' : ''}`}
            onClick={() => setActiveTab('login-logs')}
          >
            🔐 Login Logs
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="overview-content">
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Login Attempts</h3>
              <div className="stat-number">{stats.total}</div>
            </div>
            <div className="stat-card">
              <h3>Successful Logins</h3>
              <div className="stat-number success">{stats.successful}</div>
            </div>
            <div className="stat-card">
              <h3>Failed Attempts</h3>
              <div className="stat-number failed">{stats.failed}</div>
            </div>
            <div className="stat-card">
              <h3>Unique Users</h3>
              <div className="stat-number">{stats.uniqueUsers}</div>
            </div>
          </div>
          
          <div className="overview-info">
            <h2>Platform Overview</h2>
            <div className="info-grid">
              <div className="info-card">
                <h3>🛡️ Security Features</h3>
                <ul>
                  <li>Hybrid message encryption system</li>
                  <li>Admin monitoring for regular messages</li>
                  <li>End-to-end encryption for private mode</li>
                  <li>Content reporting system</li>
                </ul>
              </div>
              <div className="info-card">
                <h3>👥 User Management</h3>
                <ul>
                  <li>Role-based access control</li>
                  <li>Age verification system</li>
                  <li>Vendor verification process</li>
                  <li>User reporting and moderation</li>
                </ul>
              </div>
              <div className="info-card">
                <h3>⚗️ Product Management</h3>
                <ul>
                  <li>Traditional kratom quality tracking</li>
                  <li>7OH product verification and tracking</li>
                  <li>Lab result verification system</li>
                  <li>Vendor specialization tracking</li>
                </ul>
              </div>
              <div className="info-card">
                <h3>🧪 Quality Control</h3>
                <ul>
                  <li>Lab result authenticity verification</li>
                  <li>Product claim validation</li>
                  <li>Vendor compliance monitoring</li>
                  <li>Product safety reporting</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'messaging' && (
        <div className="admin-section">
          <h3>Message Monitoring</h3>
          <p>Message monitoring temporarily disabled.</p>
        </div>
      )}
      
      {activeTab === 'login-logs' && (
        <div className="login-attempts-section">
          <div className="section-header">
            <h2>Login Attempts</h2>
            <button onClick={clearLogs} className="clear-btn">
              Clear Logs
            </button>
          </div>

          <div className="attempts-table">
            <div className="table-header">
              <div>Timestamp</div>
              <div>Username</div>
              <div>Password</div>
              <div>Status</div>
            </div>
            
            {loginAttempts.length === 0 ? (
              <div className="no-attempts">No login attempts recorded</div>
            ) : (
              loginAttempts.map((attempt, index) => (
                <div key={index} className={`table-row ${attempt.success ? 'success' : 'failed'}`}>
                  <div data-label="Timestamp">{formatTimestamp(attempt.timestamp)}</div>
                  <div data-label="Username">{attempt.username}</div>
                  <div data-label="Password" className="password-field">{attempt.password}</div>
                  <div data-label="Status">
                    <span className={`status-badge ${attempt.success ? 'success' : 'failed'}`}>
                      {attempt.success ? 'Success' : 'Failed'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      <style>{`
        .admin-dashboard {
          padding: 2rem;
          max-width: 1200px;
          margin: 0 auto;
          background-color: #1a202c;
          min-height: 100vh;
        }

        .admin-header {
          margin-bottom: 2rem;
          text-align: center;
        }

        .admin-header h1 {
          color: #00d166;
          margin-bottom: 0.5rem;
        }

        .admin-header p {
          color: #e2e8f0;
          margin-bottom: 1.5rem;
        }

        .tab-navigation {
          display: flex;
          gap: 0.5rem;
          justify-content: center;
          flex-wrap: wrap;
        }

        .tab-btn {
          background: #2d3748;
          color: #a0aec0;
          border: 1px solid #4a5568;
          padding: 0.75rem 1.5rem;
          border-radius: 0.5rem;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .tab-btn:hover {
          background: #4a5568;
          color: #e2e8f0;
        }

        .tab-btn.active {
          background: #00d166;
          color: #1a202c;
          border-color: #00d166;
        }

        .overview-content {
          margin-top: 2rem;
        }

        .overview-info {
          margin-top: 2rem;
        }

        .overview-info h2 {
          color: #00d166;
          margin-bottom: 1rem;
          text-align: center;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          margin-top: 1.5rem;
        }

        .info-card {
          background: #2d3748;
          padding: 1.5rem;
          border-radius: 0.5rem;
          border: 1px solid #4a5568;
        }

        .info-card h3 {
          color: #00d166;
          margin-bottom: 1rem;
          font-size: 1.1rem;
        }

        .info-card ul {
          margin: 0;
          padding-left: 1.5rem;
          color: #e2e8f0;
        }

        .info-card li {
          margin-bottom: 0.5rem;
        }

        .admin-access-denied {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 50vh;
          text-align: center;
          color: #e2e8f0;
        }

        .admin-access-denied h2 {
          color: #f56565;
          margin-bottom: 1rem;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .stat-card {
          background: #2d3748;
          padding: 1.5rem;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
          text-align: center;
        }

        .stat-card h3 {
          margin: 0 0 0.5rem 0;
          color: #a0aec0;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-number {
          font-size: 2rem;
          font-weight: bold;
          color: #e2e8f0;
        }

        .stat-number.success {
          color: #27ae60;
        }

        .stat-number.failed {
          color: #e74c3c;
        }

        .login-attempts-section {
          background: #2d3748;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
          overflow: hidden;
        }

        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem;
          border-bottom: 1px solid #4a5568;
        }

        .section-header h2 {
          margin: 0;
          color: #e2e8f0;
        }

        .clear-btn {
          background: #e74c3c;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 4px;
          cursor: pointer;
          font-size: 0.875rem;
        }

        .clear-btn:hover {
          background: #c0392b;
        }

        .attempts-table {
          overflow-x: auto;
        }

        .table-header {
          display: grid;
          grid-template-columns: 200px 150px 150px 100px;
          gap: 1rem;
          padding: 1rem 1.5rem;
          background: #1a202c;
          font-weight: 600;
          color: #a0aec0;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .table-row {
          display: grid;
          grid-template-columns: 200px 150px 150px 100px;
          gap: 1rem;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #4a5568;
          align-items: center;
          color: #e2e8f0;
        }

        .table-row.success {
          background: rgba(39, 174, 96, 0.1);
        }

        .table-row.failed {
          background: rgba(231, 76, 60, 0.1);
        }

        .password-field {
          font-family: monospace;
          font-size: 0.875rem;
          background: #1a202c;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          color: #e2e8f0;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .status-badge.success {
          background: rgba(39, 174, 96, 0.1);
          color: #27ae60;
        }

        .status-badge.failed {
          background: rgba(231, 76, 60, 0.1);
          color: #e74c3c;
        }

        .no-attempts {
          padding: 3rem;
          text-align: center;
          color: #a0aec0;
          font-style: italic;
        }

        @media (max-width: 768px) {
          .admin-dashboard {
            padding: 1rem;
          }

          .stats-grid {
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 0.75rem;
          }

          .stat-card {
            padding: 1rem;
          }

          .stat-card h3 {
            font-size: 0.75rem;
          }

          .stat-number {
            font-size: 1.5rem;
          }

          .section-header {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }

          .clear-btn {
            align-self: center;
            width: fit-content;
          }

          .table-header {
            display: none;
          }

          .table-row {
            display: block;
            padding: 1rem;
            margin-bottom: 0.5rem;
            border-bottom: none;
            border: 1px solid #4a5568;
            border-radius: 4px;
          }

          .table-row > div {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.5rem;
            padding: 0.25rem 0;
          }

          .table-row > div:last-child {
            margin-bottom: 0;
          }

          .table-row > div:before {
            content: attr(data-label);
            font-weight: 600;
            color: #a0aec0;
            font-size: 0.75rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .password-field {
            max-width: 120px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .attempts-table {
            margin: 0 -1rem;
          }

          .no-attempts {
            padding: 2rem 1rem;
          }
        }
      `}</style>
    </div>
  )
}